/**
 * ImageRenderer — renders an `image` artifact (generated or fetched).
 *
 * Renders a plain `<img>` from `payload.url` (which may be a data: URI for
 * inline base64 images — both work transparently as `src`). Applies
 * `payload.width` / `payload.height` as natural pixel dimensions on the
 * element so the browser knows the intrinsic aspect ratio; CSS caps the
 * displayed size at `max-w-full h-auto` so the image scales down to fit
 * the workspace pane without distortion.
 *
 * When `payload.prompt` is present (typical for generated images) it's
 * shown below the image as a small italic, muted caption.
 *
 * Default actions when `artifact.actions` is empty:
 *   - download (id="download")
 *   - copy     (id="copy")
 *
 * The renderer just declares the actions; the dispatcher (`onAction`)
 * decides whether `copy` copies the URL or the base64 payload.
 *
 * Spec: .workspace-spec/WORKSPACE_CONTRACT.md
 */
import { useMemo, useState } from "react"

import type { Artifact, ArtifactAction, ImagePayload } from "@/api/models"

import { ImageOff } from "@/components/icons"

import { ArtifactFrame } from "./ArtifactFrame"

const DEFAULT_IMAGE_ACTIONS: ArtifactAction[] = [
  { id: "download", label: "Download", icon: "download" },
  { id: "copy", label: "Copy", icon: "copy" },
]

export interface ImageRendererProps {
  artifact: Artifact & { payload: ImagePayload }
  versionChain?: Artifact[]
  onAction?: (actionId: string, artifact: Artifact) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
}

export function ImageRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
}: ImageRendererProps) {
  const framedArtifact = useMemo<Artifact>(() => {
    const hasActions = (artifact.actions ?? []).length > 0
    const actions = hasActions ? artifact.actions : DEFAULT_IMAGE_ACTIONS
    return { ...artifact, actions }
  }, [artifact])

  const { payload } = artifact
  const [errored, setErrored] = useState(false)

  return (
    <ArtifactFrame
      artifact={framedArtifact}
      versionChain={versionChain}
      onAction={onAction}
      onSelectVersion={onSelectVersion}
      onShowHistory={onShowHistory}
      onViewTrace={onViewTrace}
    >
      <figure className="m-0 flex flex-col items-center gap-2" data-testid="image-renderer-body">
        {errored ? (
          <div
            aria-label={payload.alt ?? "Image could not be loaded."}
            className="text-muted-foreground border-border bg-muted/30 flex w-full flex-col items-center gap-2 rounded-sm border border-dashed px-4 py-8 text-sm"
            data-testid="image-renderer-error"
            role="img"
          >
            <ImageOff aria-hidden className="size-6 opacity-60" />
            <span>Image could not be loaded.</span>
          </div>
        ) : (
          <img
            alt={payload.alt ?? ""}
            className="h-auto max-w-full rounded-sm"
            data-testid="image-renderer-img"
            height={payload.height ?? undefined}
            src={payload.url}
            width={payload.width ?? undefined}
            onError={() => setErrored(true)}
          />
        )}
        {payload.prompt ? (
          <figcaption
            className="text-muted-foreground text-xs italic"
            data-testid="image-renderer-caption"
          >
            {payload.prompt}
          </figcaption>
        ) : null}
      </figure>
    </ArtifactFrame>
  )
}
